/**
 * Die Cut Shape Modal
 *
 * Custom element that renders a centered detail modal for die cut
 * shape products on the collection page. A single shared overlay
 * populates from per-card <template> elements on trigger click.
 *
 * No cart actions — information only. Image, title, description.
 */

class DieCutShapeModal extends HTMLElement {
  constructor() {
    super();
    this._trigger = null;
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
  }

  connectedCallback() {
    this._backdrop = this.querySelector('.die-cut-modal-backdrop');
    this._modalBox = this.querySelector('.die-cut-modal');
    this._closeBtn = this.querySelector('.die-cut-modal__close');
    this._imageEl = this.querySelector('.die-cut-modal__image img');
    this._titleEl = this.querySelector('.die-cut-modal__title');
    this._descriptionEl = this.querySelector('.die-cut-modal__description');

    // Close via X button
    this._closeBtn.addEventListener('click', () => this.close());

    // Close via backdrop click (not modal box click)
    this._backdrop.addEventListener('click', this._onBackdropClick);

    // Listen for trigger clicks anywhere on the page
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('.die-cut-shape-trigger');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    });
  }

  /**
   * Open the modal with content from the trigger's product card template.
   */
  open(trigger) {
    const card = trigger.closest('product-card');
    if (!card) return;

    const template = card.querySelector('template[data-shape-modal-content]');
    if (!template) return;

    const content = template.content;

    // Image
    const img = content.querySelector('[data-shape-image]');
    if (img) {
      this._imageEl.src = img.src;
      this._imageEl.alt = img.alt;
    }

    // Title
    const titleEl = content.querySelector('[data-shape-title]');
    if (titleEl) {
      this._titleEl.textContent = titleEl.textContent.trim();
    }

    // Description (body HTML)
    const descEl = content.querySelector('[data-shape-description]');
    if (descEl) {
      this._descriptionEl.innerHTML = descEl.innerHTML;
    } else {
      this._descriptionEl.innerHTML = '';
    }

    this._trigger = trigger;
    this._backdrop.classList.add('is-open');
    document.documentElement.classList.add('die-cut-modal--locked');

    // Focus the close button for keyboard users
    this._closeBtn.focus();
    document.addEventListener('keydown', this._onKeyDown);
  }

  /**
   * Close the modal, restore focus, clear content after transition.
   */
  close() {
    this._backdrop.classList.remove('is-open');
    document.documentElement.classList.remove('die-cut-modal--locked');
    document.removeEventListener('keydown', this._onKeyDown);

    // Return focus to the trigger button
    if (this._trigger) {
      this._trigger.focus();
      this._trigger = null;
    }

    // Clear content after CSS transition completes
    setTimeout(() => {
      if (!this._backdrop.classList.contains('is-open')) {
        this._imageEl.removeAttribute('src');
        this._imageEl.removeAttribute('alt');
        this._titleEl.textContent = '';
        this._descriptionEl.innerHTML = '';
      }
    }, 250);
  }

  _onKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    // Focus trap: Tab / Shift+Tab cycle within the modal
    if (event.key === 'Tab') {
      const focusable = this._modalBox.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  _onBackdropClick(event) {
    // Only close if the backdrop itself was clicked (not the modal box)
    if (event.target === this._backdrop) {
      this.close();
    }
  }
}

customElements.define('die-cut-shape-modal', DieCutShapeModal);
